import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { ChevronLeft, ChevronRight, ShoppingCart, RotateCcw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useToast } from '../components/ui/use-toast';
import { LongTermTenderVenue, VenueSelection, CreateLongTermTenderRequest } from '../types/map';
import { VenueSelectionStep } from '../components/NewLocationTender/VenueSelectionStep';
import { PlanogramStep } from '../components/NewLocationTender/PlanogramStep';
import { RetailMediaStep } from '../components/NewLocationTender/RetailMediaStep';
import { ReportingStep } from '../components/NewLocationTender/ReportingStep';
import { ReviewStep } from '../components/NewLocationTender/ReviewStep';
import { ShoppingCart as ShoppingCartComponent, CartItem } from '../components/NewLocationTender/ShoppingCart';
import { useHeaderActions } from '../components/AppLayout';

const TOTAL_STEPS = 5;

const STORAGE_KEY = 'newLocationTenderProgress';

interface SavedProgress {
  currentStep: number;
  venueSelections: VenueSelection[];
  skuSelections: { machineId: string; row: number; col: number }[];
  retailMediaSelections: Record<string, any>;
  reportingTier?: 'basic' | 'professional' | 'enterprise';
  reportingOptions?: {
    liveReporting?: boolean;
    machineBracket?: string;
    skuTier?: string;
  };
  notes: string;
}

export default function NewLocationTender() {
  // Load saved progress from localStorage
  const loadSavedProgress = (): SavedProgress | null => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (error) {
      console.error('Error loading saved progress:', error);
    }
    return null;
  };

  const savedProgress = loadSavedProgress();

  const [currentStep, setCurrentStep] = useState(savedProgress?.currentStep || 1);
  const [venues, setVenues] = useState<LongTermTenderVenue[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  // Form state - initialize from saved progress
  // Restore venue selections if we have saved progress (user is returning to an in-progress form)
  const [venueSelections, setVenueSelections] = useState<VenueSelection[]>(
    savedProgress?.venueSelections || []
  );
  const [skuSelections, setSkuSelections] = useState<{ machineId: string; row: number; col: number }[]>(savedProgress?.skuSelections || []);
  const [retailMediaSelections, setRetailMediaSelections] = useState<Record<string, any>>(savedProgress?.retailMediaSelections || {});
  const [reportingTier, setReportingTier] = useState<'basic' | 'professional' | 'enterprise' | undefined>(savedProgress?.reportingTier);
  const [reportingOptions, setReportingOptions] = useState<{
    liveReporting?: boolean;
    machineBracket?: string;
    skuTier?: string;
  }>(savedProgress?.reportingOptions || {});
  const [notes, setNotes] = useState(savedProgress?.notes || '');
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [pricePerSKU, setPricePerSKU] = useState<number>(0);
  const [retailMediaPrices, setRetailMediaPrices] = useState<Record<string, { setupPriceCents: number; recurringPriceCents: number }>>({});
  const [hasScrolledToNext, setHasScrolledToNext] = useState(false);
  const navigationButtonsRef = useRef<HTMLDivElement>(null);
  const { setHeaderActions } = useHeaderActions();

  // Save progress to localStorage whenever form state changes
  useEffect(() => {
    const progress: SavedProgress = {
      currentStep,
      venueSelections,
      skuSelections,
      retailMediaSelections,
      reportingTier,
      reportingOptions,
      notes,
    };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
    } catch (error) {
      console.error('Error saving progress:', error);
    }
  }, [currentStep, venueSelections, skuSelections, retailMediaSelections, reportingTier, reportingOptions, notes]);

  // Load venues and pricing on mount
  useEffect(() => {
    loadVenues();
    loadPricing();
  }, []);

  // Reset scroll flag when leaving step 1
  useEffect(() => {
    if (currentStep !== 1) {
      setHasScrolledToNext(false);
    }
  }, [currentStep]);

  // Auto-scroll to Next button on first selection (only for step 1)
  useEffect(() => {
    if (currentStep === 1 && venueSelections.length > 0 && !hasScrolledToNext && navigationButtonsRef.current) {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        navigationButtonsRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'end' 
        });
        setHasScrolledToNext(true);
      }, 300);
    }
  }, [venueSelections, currentStep, hasScrolledToNext]);

  const loadPricing = async () => {
    try {
      const res = await fetch('/api/brand/reservations/pricing', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        if (data.tenderPricePerSKUPerMonth) {
          setPricePerSKU(data.tenderPricePerSKUPerMonth / 100); // Convert cents to pounds
        }
      }
    } catch (err) {
      console.error('Error fetching pricing:', err);
    }
  };

  const loadVenues = async () => {
    try {
      setLoading(true);
      const res = await fetch('/api/store/long-term-tender-venues', { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load venues');
      const data = await res.json();
      setVenues(data);
    } catch (error) {
      console.error('Error loading venues:', error);
      toast({
        title: 'Error',
        description: 'Failed to load venues. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    // Validate current step before proceeding
    if (currentStep === 1) {
      if (venueSelections.length === 0) {
        toast({
          title: 'Validation Error',
          description: 'Please select at least one venue with machines.',
          variant: 'destructive',
        });
        return;
      }
    }
    
    if (currentStep < TOTAL_STEPS) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };


  // Clear saved progress on successful submission
  const clearSavedProgress = () => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing saved progress:', error);
    }
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      
      const request: CreateLongTermTenderRequest = {
        venueSelections,
        skuSelections: skuSelections.length > 0 ? skuSelections : undefined,
        retailMediaSelections: Object.keys(retailMediaSelections).length > 0 ? retailMediaSelections : undefined,
        reportingTier,
        reportingOptions: Object.keys(reportingOptions).length > 0 ? reportingOptions : undefined,
        notes: notes.trim() || undefined,
      };

      const res = await fetch('/api/brand/reservations/long-term-tender', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(request),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to submit tender request');
      }

      toast({
        title: 'Success',
        description: 'Your long-term tender request has been submitted successfully.',
      });

      // Clear saved progress
      clearSavedProgress();

      // Reset form and go back to step 1
      setCurrentStep(1);
      setVenueSelections([]);
      setSkuSelections([]);
      setRetailMediaSelections({});
      setReportingTier(undefined);
      setReportingOptions({});
      setNotes('');
      setCartItems([]);
    } catch (error) {
      console.error('Error submitting tender:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to submit tender request',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Calculate total machines and SKUs - must be before early returns
  const totalMachines = useMemo(() => {
    return venueSelections.reduce((sum, vs) => sum + vs.machineCount, 0);
  }, [venueSelections]);

  const totalSKUs = skuSelections.length;

  // Extract unique venue types from selected venues
  const selectedVenueTypes = useMemo(() => {
    const venueTypesSet = new Set<string>();
    venueSelections.forEach(vs => {
      const venue = venues.find(v => v.venueName === vs.venueName);
      if (venue && venue.venueType) {
        venueTypesSet.add(venue.venueType);
      }
    });
    return Array.from(venueTypesSet);
  }, [venueSelections, venues]);

  // Fetch retail media pricing when venue types change
  // Prices persist once fetched - only fetch when we have venue types, never clear unless starting fresh
  useEffect(() => {
    const fetchRetailMediaPricing = async () => {
      // If we have venue selections but venues haven't loaded yet, wait for them
      if (venueSelections.length > 0 && venues.length === 0) {
        return; // Don't clear prices, just wait for venues to load
      }
      
      // Only fetch if we have venue types - don't clear prices if we don't
      // Prices should persist once fetched
      if (selectedVenueTypes.length === 0) {
        return; // Keep existing prices, don't fetch or clear
      }

      try {
        const res = await fetch('/api/store/retail-media-pricing', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ venueTypes: selectedVenueTypes }),
        });

        if (res.ok) {
          const prices = await res.json();
          const priceMap: Record<string, { setupPriceCents: number; recurringPriceCents: number }> = {};
          prices.forEach((price: any) => {
            priceMap[price.itemId] = {
              setupPriceCents: price.setupPriceCents,
              recurringPriceCents: price.recurringPriceCents,
            };
          });
          setRetailMediaPrices(priceMap);
        } else {
          console.error('Failed to fetch retail media pricing');
        }
      } catch (error) {
        console.error('Error fetching retail media pricing:', error);
      }
    };

    fetchRetailMediaPricing();
  }, [selectedVenueTypes, venueSelections, venues]);

  // Update cart from venue selections (Step 1)
  useEffect(() => {
    const venueItems: CartItem[] = venueSelections.map((vs) => ({
      id: `venue-${vs.venueName}`,
      type: 'venue' as const,
      name: vs.venueName,
      category: 'Venue & Machines',
      quantity: vs.machineCount,
      unitPrice: 0, // Venues don't have a price
      totalPrice: 0,
      metadata: { machineIds: vs.machineIds },
    }));

    setCartItems((prev) => {
      const filtered = prev.filter((item) => item.type !== 'venue');
      return [...filtered, ...venueItems];
    });
  }, [venueSelections]);

  // Update cart from SKU selections (Step 2)
  useEffect(() => {
    if (skuSelections.length === 0 || pricePerSKU === 0) {
      setCartItems((prev) => prev.filter((item) => item.type !== 'sku'));
      return;
    }

    // Group SKUs by machine
    const skusByMachine = skuSelections.reduce((acc, sku) => {
      if (!acc[sku.machineId]) {
        acc[sku.machineId] = [];
      }
      acc[sku.machineId].push(sku);
      return acc;
    }, {} as Record<string, typeof skuSelections>);

    // Find machine info from venues (for duration calculation)
    const getMachineInfo = (machineId: string): { durationMonths: number } => {
      for (const venue of venues) {
        const machine = venue.machines.find((m) => m.id === machineId);
        if (machine) {
          // Parse duration from tenderTimeframe (e.g., "12 months" -> 12, "6 months" -> 6)
          let durationMonths = 12; // Default to 12 months
          if (venue.tenderTimeframe) {
            const match = venue.tenderTimeframe.match(/(\d+)\s*months?/i);
            if (match) {
              durationMonths = parseInt(match[1], 10);
            }
          }
          return { durationMonths };
        }
      }
      return { durationMonths: 12 };
    };

    // Aggregate all SKU selections into a single cart item
    const machineEntries = Object.entries(skusByMachine);
    const totalMachines = machineEntries.length;
    const totalSKUs = machineEntries.reduce((sum, [, skus]) => sum + skus.length, 0);
    
    // Calculate unique SKU positions (row, col combinations) for display
    const uniqueSKUPositions = new Set(
      skuSelections.map(s => `${s.row}-${s.col}`)
    ).size;

    // Get duration from first machine (all machines in a tier should have same duration)
    const firstMachineId = machineEntries[0]?.[0];
    const { durationMonths } = firstMachineId ? getMachineInfo(firstMachineId) : { durationMonths: 12 };

    const monthlyPrice = pricePerSKU * totalSKUs;
    const totalPrice = monthlyPrice * durationMonths;

    const skuItem: CartItem = {
      id: 'sku-aggregated',
      type: 'sku' as const,
      name: 'SKU Listings',
      category: undefined,
      quantity: totalSKUs, // Keep total for pricing calculations
      unitPrice: pricePerSKU,
      totalPrice,
      metadata: {
        totalSKUs, // Total count for pricing
        uniqueSKUPositions, // Unique positions for display
        totalMachines,
        monthlyPrice,
        durationMonths,
        allMachineIds: machineEntries.map(([machineId]) => machineId),
      },
    };

    setCartItems((prev) => {
      const filtered = prev.filter((item) => item.type !== 'sku');
      return [...filtered, skuItem];
    });
  }, [skuSelections, pricePerSKU, venues]);

  // Update cart from retail media selections (Step 3)
  useEffect(() => {
    // Extract time period from selections (default to 8 weeks if not set)
    const timePeriodWeeks = (retailMediaSelections._timePeriod as 4 | 8 | 12) || 8;
    
    // Filter out the time period key and empty/zero selections
    const actualSelections = Object.entries(retailMediaSelections)
      .filter(([key, config]) => {
        if (key === '_timePeriod' || !config || typeof config !== 'object') return false;
        const machines = typeof config.machines === 'number' ? config.machines : 0;
        const units = typeof config.units === 'number' ? config.units : 0;
        // Only include items with both machines and units greater than 0
        return machines > 0 && units > 0;
      });
    
    if (actualSelections.length === 0) {
      setCartItems((prev) => prev.filter((item) => item.type !== 'retail-media'));
      return;
    }

    // Catalog items with names and images (pricing comes from API)
    const catalogItems = [
      { id: 'dooh-ad', name: 'Digital Screen Ad', image: '/calculator-images/2.png' },
      { id: 'home-banner', name: 'Home Screen Banner', image: '/calculator-images/3.png' },
      { id: 'category-icon-lead', name: 'Category Icon Lead', image: '/calculator-images/4.png' },
      { id: 'category-banner', name: 'Category Banner', image: '/calculator-images/5.png' },
      { id: 'premium-listing', name: 'Premium Product Placement', image: '/calculator-images/6.png' },
      { id: 'category-takeover', name: 'Category Takeover', image: '/calculator-images/7.png' },
      { id: 'enhanced-detail', name: 'Enhanced Product Page', image: '/calculator-images/8.png' },
      { id: 'vend-video', name: 'Vend Video Ad', image: '/calculator-images/9.png' },
      { id: 'virtual-sku', name: 'Virtual SKU Listing', image: '/calculator-images/10.png' },
      { id: 'bundle-sku', name: 'Bundle SKU Listing', image: '/calculator-images/11.png' },
      { id: 'subscription-sku', name: 'Subscription SKU Listing', image: '/calculator-images/12.png' },
      { id: 'safe-qr', name: 'SafeQR Redirect', image: '/calculator-images/13.png' },
      { id: 'customer-survey', name: 'Customer Survey', image: '/calculator-images/14.png' },
      { id: 'customer-signup', name: 'Customer Sign-Up', image: '/calculator-images/15.png' },
    ];

    const retailMediaItems: CartItem[] = actualSelections.map(([itemId, config]: [string, any]) => {
      const item = catalogItems.find((i) => i.id === itemId);
      const machines = typeof config.machines === 'number' ? config.machines : 0;
      const units = typeof config.units === 'number' ? config.units : 0;
      const quantity = machines * units;
      
      // Get pricing from API-fetched prices
        const price = retailMediaPrices[itemId];
        const setupPriceCents = price?.setupPriceCents || 0;
        const recurringPriceCents = price?.recurringPriceCents || 0;
        
        // Setup price stays the same regardless of time period (per item, not multiplied)
        // Recurring price is per machine per unit per 4 weeks, multiplied by time period multiplier
        const timePeriodMultiplier = timePeriodWeeks / 4; // 1x for 4 weeks, 2x for 8 weeks, 3x for 12 weeks
        const recurringTotalCents = recurringPriceCents * machines * units * timePeriodMultiplier;
        const totalPrice = recurringTotalCents / 100; // Convert cents to pounds
        const unitPrice = (recurringPriceCents * timePeriodMultiplier) / 100; // Price per unit per machine for selected time period

        return {
          id: `retail-media-${itemId}`,
          type: 'retail-media' as const,
          name: item?.name || itemId,
          category: 'Retail Media',
          image: item?.image,
          quantity,
          unitPrice,
          totalPrice,
          editable: true,
          metadata: { 
            itemId, 
            machines, 
            units, 
            setupPrice: setupPriceCents / 100, // Convert to pounds (stays same for all time periods)
            recurringPrice: recurringPriceCents / 100, // Base price per unit per machine per 4 weeks
            timePeriodWeeks,
        },
      };
    });

    setCartItems((prev) => {
      const filtered = prev.filter((item) => item.type !== 'retail-media');
      return [...filtered, ...retailMediaItems];
    });
  }, [retailMediaSelections, retailMediaPrices]);

  // Update cart from reporting selections (Step 4)
  useEffect(() => {
    if (!reportingTier) {
      setCartItems((prev) => prev.filter((item) => item.type !== 'reporting'));
      return;
    }

    const getMachineBracket = (machineCount: number): string => {
      if (machineCount <= 25) return '1-25';
      if (machineCount <= 100) return '26-100';
      if (machineCount <= 250) return '101-250';
      return '251+';
    };

    const calculatePricing = (
      tier: string,
      machineBracket: string,
      skuTier: string,
      liveReporting: boolean = false
    ) => {
      const pricing = {
        basic: { '1-25': 250, '26-100': 500, '101-250': 750, '251+': 1000 },
        professional: { '1-25': 500, '26-100': 1000, '101-250': 1500, '251+': 2000 },
        enterprise: { '1-25': 1000, '26-100': 2000, '101-250': 3000, '251+': 4000 },
      };

      const basePrice = pricing[tier as keyof typeof pricing][machineBracket as keyof typeof pricing.basic] || 0;
      let skuAddOn = 0;
      if (skuTier === '11-20') skuAddOn = 500;

      let liveReportingAddOn = 0;
      if (liveReporting) {
        if (tier === 'basic') liveReportingAddOn = 250;
        else if (tier === 'professional') liveReportingAddOn = 750;
      }

      return basePrice + skuAddOn + liveReportingAddOn;
    };

    const machineBracket = getMachineBracket(totalMachines);
    const skuTier = reportingOptions.skuTier || '1-10';
    const liveReporting = reportingOptions.liveReporting || false;
    const monthlyPrice = calculatePricing(reportingTier, machineBracket, skuTier, liveReporting);
    const durationMonths = 12; // Reporting packages are 12 months
    const totalPrice = monthlyPrice * durationMonths;

    const reportingItem: CartItem = {
      id: 'reporting-tier',
      type: 'reporting' as const,
      name: `Reporting & Analytics - ${reportingTier.charAt(0).toUpperCase() + reportingTier.slice(1)}`,
      category: 'Reporting Package',
      quantity: 1,
      unitPrice: monthlyPrice,
      totalPrice,
      metadata: { 
        tier: reportingTier, 
        machineBracket, 
        skuTier, 
        liveReporting,
        monthlyPrice,
        durationMonths,
      },
    };

    setCartItems((prev) => {
      const filtered = prev.filter((item) => item.type !== 'reporting');
      return [...filtered, reportingItem];
    });
  }, [reportingTier, reportingOptions, totalMachines]);

  // Helper function to format numbers with commas
  const formatNumber = (num: number): string => {
    return num.toLocaleString('en-US');
  };

  // Helper function to format currency with commas
  const formatCurrency = (num: number): string => {
    return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Exclude venue items and retail-media items (until step 3) from cart count
  const displayItemsForCount = cartItems.filter(item => {
    if (item.type === 'venue') return false;
    if (item.type === 'retail-media' && currentStep < 3) return false;
    return true;
  });
  
  const cartItemCount = displayItemsForCount.reduce((sum, item) => sum + item.quantity, 0);
  const cartTotal = displayItemsForCount.reduce((sum, item) => sum + item.totalPrice, 0);
  
  // Calculate setup fees for retail media
  const setupFees = cartItems
    .filter(item => item.type === 'retail-media' && item.metadata?.setupPrice)
    .reduce((sum, item) => {
      const setupPrice = item.metadata?.setupPrice || 0;
      const machines = item.metadata?.machines || 1;
      const units = item.metadata?.units || item.quantity;
      return sum + (setupPrice * machines * units);
    }, 0);
  
  const cartGrandTotal = cartTotal + setupFees;

  // Restart function to clear all selections
  const handleRestart = useCallback(() => {
    if (window.confirm('Are you sure you want to restart? This will clear all your selections across all steps.')) {
      clearSavedProgress();
      setCurrentStep(1);
      setVenueSelections([]);
      setSkuSelections([]);
      setRetailMediaSelections({});
      setReportingTier(undefined);
      setReportingOptions({});
      setNotes('');
      setCartItems([]);
      setPricePerSKU(0);
      setHasScrolledToNext(false);
      
      toast({
        title: 'Restarted',
        description: 'All selections have been cleared. Starting fresh.',
      });
    }
  }, [toast]);

  // Update header actions whenever cart changes
  useEffect(() => {
    setHeaderActions(
      <>
        <Button
          variant="outline"
          size="lg"
          onClick={handleRestart}
          className="flex items-center gap-2"
        >
          <RotateCcw className="w-4 h-4" />
          <span className="hidden sm:inline">Restart</span>
        </Button>
        {cartItemCount > 0 ? (
          <button
            onClick={() => setCartOpen(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 transition-colors text-sm font-medium text-slate-700"
          >
            <ShoppingCart className="w-4 h-4" />
            <span>£{formatCurrency(cartGrandTotal)}</span>
          </button>
        ) : (
          <Button
            variant="outline"
            size="lg"
            onClick={() => setCartOpen(true)}
            className="flex items-center gap-2"
          >
            <ShoppingCart className="w-5 h-5" />
            <span>Cart</span>
          </Button>
        )}
      </>
    );

    // Cleanup: clear header actions when component unmounts
    return () => {
      setHeaderActions(null);
    };
  }, [cartItemCount, cartGrandTotal, handleRestart, setHeaderActions, setCartOpen]);

  const handleCartQuantityChange = (itemId: string, newQuantity: number) => {
    // Only allow quantity changes for retail media items
    setCartItems((prev) =>
      prev.map((item) => {
        if (item.id === itemId && item.editable && item.type === 'retail-media') {
          const quantityDelta = newQuantity - item.quantity;
          const machines = item.metadata?.machines || 1;
          const oldUnits = item.metadata?.units || item.quantity;
          
          // Calculate new units based on new quantity and machines
          // quantity = machines * units, so units = quantity / machines
          const newUnits = Math.max(1, Math.round(newQuantity / machines));
          const newTotalPrice = item.unitPrice * newQuantity;
          
          // Update retailMediaSelections to sync back to step 3
          const retailItemId = item.metadata?.itemId;
          if (retailItemId) {
            setRetailMediaSelections((prevSelections) => {
              const updated = { ...prevSelections };
              if (updated[retailItemId]) {
                updated[retailItemId] = {
                  ...updated[retailItemId],
                  units: newUnits,
                };
              }
              return updated;
            });
          }
          
          return {
            ...item,
            quantity: newQuantity,
            totalPrice: newTotalPrice,
            metadata: {
              ...item.metadata,
              units: newUnits,
            },
          };
        }
        return item;
      })
    );
  };

  if (loading) {
    return (
      <div className="w-full px-2 md:px-4 py-4 md:py-8">
        <div className="text-center">Loading venues...</div>
      </div>
    );
  }

  return (
      <div className="w-full px-2 md:px-4 py-4 md:py-8">

      {/* Progress Indicator */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          {[1, 2, 3, 4, 5].map((step) => (
            <div key={step} className="flex items-center flex-1">
              <div className="flex flex-col items-center flex-1">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                    step === currentStep
                      ? 'bg-blue-600 text-white'
                      : step < currentStep
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-200 text-gray-600'
                  }`}
                >
                  {step < currentStep ? '✓' : step}
                </div>
                <div className="mt-2 text-xs text-center max-w-[100px]">
                  {step === 1 && 'Select Venues'}
                  {step === 2 && 'Planogram & SKUs'}
                  {step === 3 && 'Retail Media'}
                  {step === 4 && 'Reporting'}
                  {step === 5 && 'Review & Submit'}
                </div>
              </div>
              {step < TOTAL_STEPS && (
                <div
                  className={`h-1 flex-1 mx-2 ${
                    step < currentStep ? 'bg-green-500' : 'bg-gray-200'
                  }`}
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Step Content */}
      <div className="bg-white rounded-lg border border-slate-200 p-6 mb-6 min-h-[500px]">
        {currentStep === 1 && (
          <VenueSelectionStep
            venues={venues}
            venueSelections={venueSelections}
            onVenueSelectionsChange={setVenueSelections}
          />
        )}
        {currentStep === 2 && (
          <PlanogramStep
            venueSelections={venueSelections}
            venues={venues}
            skuSelections={skuSelections}
            onSkuSelectionsChange={setSkuSelections}
            pricePerSKU={pricePerSKU}
            onPricePerSKUChange={setPricePerSKU}
          />
        )}
        {currentStep === 3 && (
          <RetailMediaStep
            venueSelections={venueSelections}
            skuSelections={skuSelections}
            retailMediaSelections={retailMediaSelections}
            onRetailMediaSelectionsChange={setRetailMediaSelections}
            venues={venues}
          />
        )}
        {currentStep === 4 && (
          <ReportingStep
            venueSelections={venueSelections}
            reportingTier={reportingTier}
            reportingOptions={reportingOptions}
            onReportingTierChange={setReportingTier}
            onReportingOptionsChange={setReportingOptions}
          />
        )}
        {currentStep === 5 && (
          <ReviewStep
            venueSelections={venueSelections}
            skuSelections={skuSelections}
            retailMediaSelections={retailMediaSelections}
            reportingTier={reportingTier}
            reportingOptions={reportingOptions}
            notes={notes}
            onNotesChange={setNotes}
            venues={venues}
          />
        )}
      </div>

      {/* Navigation Buttons */}
      <div ref={navigationButtonsRef} className="flex justify-between">
        <Button
          variant="outline"
          onClick={handleBack}
          disabled={currentStep === 1}
          className="flex items-center gap-2"
        >
          <ChevronLeft className="w-4 h-4" />
          Back
        </Button>
        {currentStep < TOTAL_STEPS ? (
          <Button 
            onClick={handleNext} 
            className={`flex items-center gap-2 ${
              venueSelections.length > 0
                ? 'fixed bottom-6 right-6 z-50 bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl hover:scale-105 px-6 py-3 text-base font-semibold animate-in fade-in slide-in-from-bottom-4 duration-300'
                : ''
            }`}
          >
            Next
            <ChevronRight className="w-4 h-4" />
          </Button>
        ) : (
          <Button 
            onClick={handleSubmit} 
            disabled={submitting} 
            className="fixed bottom-6 right-6 z-50 bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl px-8 py-4 text-base font-semibold rounded-lg transition-all duration-300 hover:scale-105"
            style={{
              animation: submitting ? 'none' : 'pulse 2s ease-in-out infinite'
            }}
          >
            {submitting ? 'Submitting...' : 'Submit Request'}
          </Button>
        )}
      </div>

      {/* Shopping Cart */}
      <ShoppingCartComponent
        open={cartOpen}
        onOpenChange={setCartOpen}
        items={cartItems}
        onQuantityChange={handleCartQuantityChange}
        currentStep={currentStep}
      />
    </div>
  );
}







